import React, { useEffect, createElement } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
interface StructuredDataProps {
  page: string;
}
function injectJsonLd(id: string, data: object) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
function removeJsonLd(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}
const BASE_URL = 'https://astroguide.ru';
function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'AstroGuide',
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    description: 'Платформа для глубокого самопознания через профессиональную астрологию',
    sameAs: ['https://t.me/astroguide', 'https://vk.com/astroguide'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Russian', 'English', 'Chinese', 'Spanish']
    }
  };
}
function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'AstroGuide',
    url: BASE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/#/interpretations?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
}
function getBreadcrumbSchema(items: {
  name: string;
  url: string;
}[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
}
function getServiceSchema(name: string, description: string, price: string, currency: string = 'RUB') {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Astrological Report',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: 'AstroGuide',
      url: BASE_URL
    },
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency: currency,
      availability: 'https://schema.org/InStock'
    }
  };
}
function getFAQSchema(questions: {
  q: string;
  a: string;
}[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };
}
const SERVICE_DATA: Record<string, {
  name: string;
  description: string;
  price: string;
}> = {
  'service-natal': {
    name: 'Натальная карта — Полный анализ личности',
    description: 'Подробный отчёт по натальной карте: характер, таланты, карьера, отношения, здоровье.',
    price: '3900'
  },
  'service-solar': {
    name: 'Соляр — Прогноз на год',
    description: 'Прогноз на ваш персональный год: ключевые темы, возможности и вызовы.',
    price: '2900'
  },
  'service-compatibility': {
    name: 'Совместимость (Синастрия)',
    description: 'Анализ совместимости для пар: характеры, эмоциональная связь, потенциал отношений.',
    price: '3900'
  },
  'service-combo': {
    name: 'Комбо: Натальная карта + Соляр',
    description: 'Два отчёта в одном: глубокий анализ личности и прогноз на год.',
    price: '5900'
  },
  'service-forecast': {
    name: 'Прогноз по транзитам',
    description: 'Персональный прогноз: важные даты, благоприятные и сложные периоды.',
    price: '2900'
  },
  'service-consultation': {
    name: 'Консультация с астрологом',
    description: 'Персональная консультация с проверенным астрологом по видеозвонку или в чате.',
    price: '5000'
  }
};
const FAQ_DATA = [{
  q: 'Что такое натальная карта?',
  a: 'Натальная карта — это карта неба на момент вашего рождения, которая показывает расположение планет в знаках зодиака и домах. Она раскрывает ваш характер, таланты, потенциал и жизненные задачи.'
}, {
  q: 'Как быстро я получу отчёт?',
  a: 'Автоматические отчёты генерируются мгновенно после оплаты. Вы получите PDF на email в течение 1-2 минут.'
}, {
  q: 'Нужно ли знать точное время рождения?',
  a: 'Для максимально точного анализа желательно знать время рождения с точностью до 15 минут. Без времени рождения невозможно определить Асцендент и систему домов.'
}, {
  q: 'Можно ли получить возврат?',
  a: 'Да, мы предоставляем возврат в течение 14 дней, если отчёт не был скачан. Для консультаций — отмена за 24 часа до сеанса.'
}, {
  q: 'Чем ваши отчёты отличаются от бесплатных?',
  a: 'Наши отчёты используют швейцарские эфемериды для точных расчётов и содержат 30-50 страниц глубокого персонализированного анализа, а не общие описания знаков.'
}];
export function StructuredData({
  page
}: StructuredDataProps) {
  const {
    language
  } = useLanguage();
  useEffect(() => {
    // Always inject Organization + WebSite
    injectJsonLd('ld-organization', getOrganizationSchema());
    injectJsonLd('ld-website', getWebSiteSchema());
    // Breadcrumbs
    const breadcrumbMap: Record<string, {
      name: string;
      url: string;
    }[]> = {
      home: [{
        name: 'Главная',
        url: BASE_URL
      }],
      'service-natal': [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Услуги',
        url: `${BASE_URL}/#/marketplace`
      }, {
        name: 'Натальная карта',
        url: `${BASE_URL}/#/service-natal`
      }],
      'service-solar': [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Услуги',
        url: `${BASE_URL}/#/marketplace`
      }, {
        name: 'Соляр',
        url: `${BASE_URL}/#/service-solar`
      }],
      'service-compatibility': [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Услуги',
        url: `${BASE_URL}/#/marketplace`
      }, {
        name: 'Совместимость',
        url: `${BASE_URL}/#/service-compatibility`
      }],
      'service-combo': [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Услуги',
        url: `${BASE_URL}/#/marketplace`
      }, {
        name: 'Комбо',
        url: `${BASE_URL}/#/service-combo`
      }],
      'service-forecast': [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Услуги',
        url: `${BASE_URL}/#/marketplace`
      }, {
        name: 'Прогноз',
        url: `${BASE_URL}/#/service-forecast`
      }],
      'service-consultation': [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Услуги',
        url: `${BASE_URL}/#/marketplace`
      }, {
        name: 'Консультация',
        url: `${BASE_URL}/#/service-consultation`
      }],
      pricing: [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Цены',
        url: `${BASE_URL}/#/pricing`
      }],
      astrologers: [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Астрологи',
        url: `${BASE_URL}/#/astrologers`
      }],
      interpretations: [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Знания',
        url: `${BASE_URL}/#/interpretations`
      }, {
        name: 'Энциклопедия',
        url: `${BASE_URL}/#/interpretations`
      }],
      calendar: [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Знания',
        url: `${BASE_URL}/#/interpretations`
      }, {
        name: 'Астро-календарь',
        url: `${BASE_URL}/#/calendar`
      }],
      blog: [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Знания',
        url: `${BASE_URL}/#/interpretations`
      }, {
        name: 'Блог',
        url: `${BASE_URL}/#/blog`
      }],
      'free-chart': [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Бесплатный расчёт',
        url: `${BASE_URL}/#/free-chart`
      }],
      faq: [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'FAQ',
        url: `${BASE_URL}/#/faq`
      }],
      about: [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'О нас',
        url: `${BASE_URL}/#/about`
      }],
      contact: [{
        name: 'Главная',
        url: BASE_URL
      }, {
        name: 'Контакты',
        url: `${BASE_URL}/#/contact`
      }]
    };
    if (breadcrumbMap[page]) {
      injectJsonLd('ld-breadcrumb', getBreadcrumbSchema(breadcrumbMap[page]));
    } else {
      removeJsonLd('ld-breadcrumb');
    }
    // Service schema for service pages
    if (SERVICE_DATA[page]) {
      const s = SERVICE_DATA[page];
      injectJsonLd('ld-service', getServiceSchema(s.name, s.description, s.price));
    } else {
      removeJsonLd('ld-service');
    }
    // FAQ schema
    if (page === 'faq') {
      injectJsonLd('ld-faq', getFAQSchema(FAQ_DATA));
    } else {
      removeJsonLd('ld-faq');
    }
    return () => {
      // Cleanup page-specific schemas on unmount
      removeJsonLd('ld-breadcrumb');
      removeJsonLd('ld-service');
      removeJsonLd('ld-faq');
    };
  }, [page, language]);
  return null;
}